import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "./config";
import "./PortalHome.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import {
  Container,
  Row,
  Col,
  Card as BSCard,
  Button,
  Tabs,
  Tab,
} from "react-bootstrap";

// distance (km)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const PortalHome = () => {
  const [locations, setLocations] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [badges, setBadges] = useState(null);
  const [pdcs, setPdcs] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE}/api/locations?pagination[pageSize]=1000&populate=*`,
        );
        const sourceArray = res.data.data ?? res.data ?? [];
        const rawItems = Array.isArray(sourceArray) ? sourceArray : [];
        const items = rawItems
          .map((it) => {
            const attrs = it.attributes ?? it;
            const latCandidates = [
              attrs.latitude,
              attrs.LATITUDE,
              attrs.lat,
              attrs.Latitude,
            ];
            const lonCandidates = [
              attrs.longitude,
              attrs.LONGITUDE,
              attrs.lon,
              attrs.Longitude,
            ];
            let latRaw = latCandidates.find(
              (v) => v !== undefined && v !== null,
            );
            let lonRaw = lonCandidates.find(
              (v) => v !== undefined && v !== null,
            );

            const geom =
              attrs.geometry ?? it.geometry ?? attrs.geom ?? attrs.SHAPE;
            if ((latRaw === undefined || lonRaw === undefined) && geom) {
              const coords =
                geom.coordinates ??
                (geom.geometries ? geom.geometries[0]?.coordinates : undefined);
              if (Array.isArray(coords)) {
                lonRaw = lonRaw ?? coords[0];
                latRaw = latRaw ?? coords[1];
              }
            }

            const latitude =
              latRaw !== undefined && latRaw !== null
                ? parseFloat(latRaw)
                : NaN;
            const longitude =
              lonRaw !== undefined && lonRaw !== null
                ? parseFloat(lonRaw)
                : NaN;

            return { id: it.id ?? attrs.id, ...attrs, latitude, longitude };
          })
          .filter(
            (i) => Number.isFinite(i.latitude) && Number.isFinite(i.longitude),
          );
        setLocations(items);
        try {
          console.info("PortalHome: fetched items count", items.length);
          if (items.length > 0)
            console.info(
              "PortalHome: example item keys",
              Object.keys(items[0] || {}),
            );
          const found315 = items.find((it) => it.id === 315 || it.id === "315");
          if (found315) console.info("PortalHome: fetched item 315", found315);
          else
            console.info("PortalHome: item 315 not present in fetched items");
        } catch (e) {
          /* ignore */
        }
      } catch (err) {
        console.error("Portal fetch failed", err);
      }
    };
    fetch();

    const fetchBadges = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE}/api/badges?pagination[pageSize]=1000&populate=*`,
        );
        const sourceArray = res.data.data ?? res.data ?? [];
        const rawItems = Array.isArray(sourceArray) ? sourceArray : [];
        const items = rawItems.map((it) => {
          const attrs = it.attributes ?? it;
          return { id: it.id ?? attrs.id, ...attrs };
        });
        setBadges(items);
      } catch (err) {
        console.error("Badge fetch failed", err);
        setBadges([]);
      }
    };

    fetchBadges();

    const fetchPdcs = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE}/api/pdcs?pagination[pageSize]=1000&populate=*`,
        );
        const sourceArray = res.data.data ?? res.data ?? [];
        const rawItems = Array.isArray(sourceArray) ? sourceArray : [];
        const items = rawItems.map((it) => {
          const attrs = it.attributes ?? it;
          return { id: it.id ?? attrs.id, ...attrs };
        });
        setPdcs(items);
      } catch (err) {
        console.error("PDC fetch failed", err);
        setPdcs([]);
      }
    };

    const fetchArticles = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE}/api/articles?pagination[pageSize]=1000&populate=*`,
        );
        const sourceArray = res.data.data ?? res.data ?? [];
        const rawItems = Array.isArray(sourceArray) ? sourceArray : [];
        const items = rawItems.map((it) => {
          const attrs = it.attributes ?? it;
          return { id: it.id ?? attrs.id, ...attrs };
        });
        try {
          console.info("PortalHome: fetched articles count", items.length);
          if (items.length > 0) console.info("PortalHome: example article keys", Object.keys(items[0] || {}));
          if (items.length > 0) console.info("PortalHome: example article image field", items[0].image || items[0].images || items[0].photos);
        } catch (e) {}
        setArticles(items);
      } catch (err) {
        console.error("Article fetch failed", err);
        setArticles([]);
      }
    };

    fetchPdcs();
    fetchArticles();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setUserLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => {},
      );
    }
  }, []);

  const petParks = locations.filter(
    (l) => (l.facilityTypeEn || "").trim() === "PET GARDEN",
  );
  const restaurants = locations.filter(
    (l) => (l.facilityTypeEn || "").trim() === "Restaurant",
  );
  const clinics = locations.filter((l) => {
    const ftEn = (l.facilityTypeEn || "").toString().toLowerCase();
    const ftTc = (l.facilityTypeTc || "").toString().toLowerCase();
    return (
      ftEn.includes("vet") ||
      ftEn.includes("clinic") ||
      ftTc.includes("獸醫") ||
      ftTc.includes("診所")
    );
  });

  const withDistance = (arr) =>
    arr.map((l) => ({
      ...l,
      distance: userLocation
        ? calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            l.latitude,
            l.longitude,
          )
        : null,
    }));

  const nearbyParks = withDistance(petParks)
    .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999))
    .slice(0, 8);
  const nearbyRestaurants = withDistance(restaurants)
    .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999))
    .slice(0, 8);
  const nearbyClinics = withDistance(clinics)
    .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999))
    .slice(0, 8);
  const recentRestaurants = restaurants
    .slice()
    .sort((a, b) => {
      const ta = a.createdAt || a.publishedAt || a.updatedAt || a.id;
      const tb = b.createdAt || b.publishedAt || b.updatedAt || b.id;
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return new Date(tb).valueOf() - new Date(ta).valueOf();
    })
    .slice(0, 8);

  const recentPdcs = pdcs
    .slice()
    .sort((a, b) => {
      const ta = a.createdAt || a.publishedAt || a.updatedAt || a.id;
      const tb = b.createdAt || b.publishedAt || b.updatedAt || b.id;
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return new Date(tb).valueOf() - new Date(ta).valueOf();
    })
    .slice(0, 8);

  const recentArticles = articles
    .slice()
    .sort((a, b) => {
      const ta = a.createdAt || a.publishedAt || a.updatedAt || a.id;
      const tb = b.createdAt || b.publishedAt || b.updatedAt || b.id;
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return new Date(tb).valueOf() - new Date(ta).valueOf();
    })
    .slice(0, 8);

  // 10 個 Badge placeholder（暫時的空白 card）
  const badgePlaceholders = Array.from({ length: 10 }, (_, i) => ({
    id: `badge-placeholder-${i}`,
    image: "",
    name: "",
    info: "",
  }));

  // 自適應顯示數量（依視窗寬度計算每行可放多少張 card）
  const [badgeDisplayCount, setBadgeDisplayCount] = useState(() => {
    if (typeof window === "undefined") return 8;
    const w = window.innerWidth || 1024;
    return Math.max(1, Math.floor(w / 240));
  });

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth || 1024;
      setBadgeDisplayCount(Math.max(1, Math.floor(w / 240)));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  // combine fetched badges with placeholders to render `badgeDisplayCount` items
  const badgeItems = (() => {
    const MAX = badgeDisplayCount || 8;
    const real = Array.isArray(badges) && badges.length > 0 ? badges.slice(0, MAX) : [];
    if (real.length >= MAX) return real.slice(0, MAX);
    const need = MAX - real.length;
    const fillers = badgePlaceholders.slice(0, need);
    return [...real, ...fillers];
  })();

  // Tabs state and mapping for replacing multiple PortalRow blocks
  const [activeTab, setActiveTab] = useState("parks");
  const tabs = [
    { key: "parks", label: "附近寵物公園", items: nearbyParks },
    { key: "clinics", label: "附近診所", items: nearbyClinics },
    { key: "restaurants", label: "附近餐廳", items: nearbyRestaurants },
    {
      key: "recentRestaurants",
      label: "最近新增的餐廳",
      items: recentRestaurants,
    },
  ];

  // normalize and resolve image URL, with explicit handling for photos array
  function normalizeUrl(found) {
    try {
      if (!found) return "/default-cover.svg";
      if (found.startsWith("http")) return found;
      const base =
        config.API_URL &&
        typeof config.API_URL === "string" &&
        config.API_URL.trim() !== ""
          ? config.API_URL.replace(/\/$/, "")
          : window.location.origin;
      return new URL(found, base).toString();
    } catch (err) {
      console.warn("getCoverImage: failed to normalize", found, err);
      return found || "/default-cover.svg";
    }
  }

  function getCoverImage(it) {
    const fallback = "/default-cover.svg";
    if (!it) return fallback;

    // Strapi v4 single media object: { data: { attributes: { url, formats } } }
    try {
      const checkSingleData =
        it.image?.data ?? it.icon?.data ?? it.thumbnail?.data ?? it.logo?.data ?? it.avatar?.data;
      if (checkSingleData) {
        const node = Array.isArray(checkSingleData) ? checkSingleData[0] : checkSingleData;
        const attrs = node.attributes ?? node;
        if (attrs.formats) {
          const f = attrs.formats;
          const url = f.small?.url || f.medium?.url || f.large?.url || f.thumbnail?.url;
          if (url) return normalizeUrl(url);
        }
        if (attrs.url) return normalizeUrl(attrs.url);
      }
    } catch (err) {
      // ignore and continue
    }

    // handle single media object (Strapi media) e.g. item.image = { formats: {...}, url: '/uploads/..' }
    try {
      const single = it.image || it.icon || it.thumbnail || it.logo || it.avatar || it.imageUrl;
      if (single && typeof single === "object") {
        const a = single.attributes ?? single;
        if (a.formats) {
          const f = a.formats;
          const url = f.small?.url || f.medium?.url || f.large?.url || f.thumbnail?.url;
          if (url) return normalizeUrl(url);
        }
        if (a.url) return normalizeUrl(a.url);
        if (single.url) return normalizeUrl(single.url);
      }
    } catch (err) {
      // ignore and continue to other checks
    }

    // explicit photos handling
    try {
      let first = null;
      if (Array.isArray(it.photos) && it.photos.length > 0)
        first = it.photos[0];
      else if (
        it.photos &&
        Array.isArray(it.photos.data) &&
        it.photos.data.length > 0
      )
        first = it.photos.data[0];
      else if (Array.isArray(it.images) && it.images.length > 0)
        first = it.images[0];
      else if (
        it.images &&
        Array.isArray(it.images.data) &&
        it.images.data.length > 0
      )
        first = it.images.data[0];

      if (first) {
        const attrs = first.attributes || first;
        if (attrs.formats) {
          const f = attrs.formats;
          const url =
            f.small?.url || f.medium?.url || f.large?.url || f.thumbnail?.url;
          if (url) return normalizeUrl(url);
        }
        if (attrs.url) return normalizeUrl(attrs.url);
        if (first.url) return normalizeUrl(first.url);
      }
    } catch (err) {
      // continue to generic checks
    }

    // generic fallback checks
    const tryPaths = [];
    // common single-field locations
    tryPaths.push(
      it.image || it.coverImage || it.cover || it.photo || it.photos,
    );
    tryPaths.push(it.images || it.pictures || it.media);
    // additional common keys (icons, thumbnails, logos)
    tryPaths.push(it.icon || it.thumbnail || it.logo || it.avatar || it.imageUrl);
    const candidates = [];

    const pushIfStr = (v) => {
      if (typeof v === "string" && v.trim()) candidates.push(v);
    };
    const extractFromArray = (arr) => {
      if (!Array.isArray(arr)) return;
      for (const el of arr) {
        if (!el) continue;
        if (typeof el === "string") candidates.push(el);
        else if (el.url) candidates.push(el.url);
        else if (
          el.attributes &&
          (el.attributes.url || el.attributes.formats)
        ) {
          candidates.push(
            el.attributes.url ||
              (el.attributes.formats &&
                (el.attributes.formats.large?.url ||
                  el.attributes.formats.small?.url)),
          );
        }
      }
    };

    for (const p of tryPaths) {
      if (!p) continue;
      if (typeof p === "string") pushIfStr(p);
      else if (Array.isArray(p)) extractFromArray(p);
      else if (p.data) {
        const arr = Array.isArray(p.data) ? p.data : [p.data];
        for (const d of arr) {
          if (!d) continue;
          if (d.attributes) {
            const a = d.attributes;
            if (a.url) candidates.push(a.url);
            if (a.formats) {
              const fmt = a.formats;
              candidates.push(
                fmt.large?.url ||
                  fmt.medium?.url ||
                  fmt.small?.url ||
                  fmt.thumbnail?.url,
              );
            }
          }
          if (d.url) candidates.push(d.url);
        }
      } else if (p.attributes && p.attributes.url) {
        candidates.push(p.attributes.url);
      }
    }

    if (it.imageUrl) candidates.push(it.imageUrl);
    if (it.images && typeof it.images === "string") candidates.push(it.images);

    const found = candidates.find(Boolean);
    if (!found) {
      try {
        console.info(
          "getCoverImage: no image found for",
          it.id,
          "photos=",
          it.photos,
        );
      } catch (e) {}
      return fallback;
    }

    return normalizeUrl(found);
  }

  const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
  const ItemCard = ({ item, hideMapButton = false }) => {
    const src = getCoverImage(item) || "/default-cover.svg";
    const titleText = item?.title || item?.nameTc || item?.nameEn || item?.name || "";
    return (
      <BSCard style={{ width: 220, marginRight: 12 }}>
        <BSCard.Img
          variant="top"
          src={src}
          alt={titleText || "cover"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/default-cover.svg";
          }}
        />
        <BSCard.Body
          style={{ display: "flex", flexDirection: "column", height: 160 }}
        >
          <BSCard.Title style={{ fontSize: "1rem" }}>
            {titleText}
          </BSCard.Title>
          {(() => {
            const metaLine1 = item.addressTc || item.addressEn || (item.title ? null : item.alias);
            return metaLine1 ? (
              <div className="meta" style={{ fontSize: 12 }}>{metaLine1}</div>
            ) : null;
          })()}
          <div className="meta" style={{ fontSize: 12 }}>
            {item.facilityDetailsTc || item.facilityDetailsEn || (item.content ? (typeof item.content === 'string' ? item.content : (item.content[0]?.children?.[0]?.text || '')) : '')}
          </div>
          {item.distance != null && (
            <div className="distance" style={{ marginTop: 6 }}>
              {item.distance.toFixed(1)} km
            </div>
          )}
          {!hideMapButton && (
              <div style={{ marginTop: "auto", display: "flex", gap: 4 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (item && item.latitude && item.longitude) {
                    navigate(`/map?lat=${item.latitude}&lng=${item.longitude}`);
                  } else {
                    navigate("/map");
                  }
                }}
              >
                在地圖顯示
              </Button>
                {isAuthenticated && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      if (item && item.latitude && item.longitude) {
                        navigate(`/map?lat=${item.latitude}&lng=${item.longitude}&locId=${item.id}`);
                      } else {
                        navigate(`/location/${item?.documentId || item?.id}`); // Fallback to original detail page if coords are missing
                      }
                    }}
                  >
                    詳情
                  </Button>
                )}
            </div>
          )}
        </BSCard.Body>
      </BSCard>
    );
  };

  // 簡潔的 Badge 卡片（空白 placeholder 樣式）
  const BadgeCard = ({ item }) => {
    const titleText =
      item?.title || item?.name || item?.label || item?.nameEn || item?.titleEn || "";
    const descText =
      item?.description || item?.info || item?.summary || item?.subtitle || item?.descriptionEn || "";
    const src = getCoverImage(item);

    return (
      <BSCard
        className="badge-card card"
        style={{
          width: 220,
          marginRight: 12,
          minHeight: 160,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {src ? (
          <div style={{ width: "100%", paddingTop: "100%", position: "relative", background: "#fff", borderRadius: 6, overflow: 'hidden' }}>
            <img
              src={src}
              alt={titleText || "badge"}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/default-cover.svg";
              }}
            />
          </div>
        ) : (
          <div style={{ width: "100%", paddingTop: "100%", background: "#f5f5f5", borderRadius: 6 }} />
        )}

        <BSCard.Body style={{ padding: 8, flex: "1 0 auto", textAlign: "center" }}>
          <BSCard.Title style={{ fontSize: 14, marginBottom: 4, textAlign: "center" }}>
            {titleText}
          </BSCard.Title>
          {descText ? (
            <div style={{ fontSize: 12, color: "#666", textAlign: "center" }}>{descText}</div>
          ) : null}
        </BSCard.Body>
      </BSCard>
    );
  };

  // 橫向拖曳與箭頭操作的 PortalRow
  function PortalRow({ title, items, hideMapButton = false }) {
    const cardsRef = React.useRef(null);
    const [dragging, setDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragScrollStart, setDragScrollStart] = useState(0);
    const [showArrows, setShowArrows] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // 取得單一 card 的闊度
    const getCardWidth = () => {
      if (cardsRef.current) {
        const card = cardsRef.current.querySelector(".card");
        if (card) return card.offsetWidth + 12; // card 闊度 + gap
      }
      return 220; // fallback
    };

    const scrollBy = (dx) => {
      if (cardsRef.current) {
        cardsRef.current.scrollLeft += dx;
      }
    };

    const handleLeft = () => scrollBy(-getCardWidth());
    const handleRight = () => scrollBy(getCardWidth());

    // 拖曳操作
    const onMouseDown = (e) => {
      setDragging(true);
      setDragStartX(e.clientX);
      setDragScrollStart(cardsRef.current.scrollLeft);
    };

    // 手機版拖曳：touch event
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        setDragging(true);
        setDragStartX(e.touches[0].clientX);
        setDragScrollStart(cardsRef.current.scrollLeft);
      }
    };
    const onTouchMove = (e) => {
      if (!dragging || e.touches.length !== 1) return;
      const dx = dragStartX - e.touches[0].clientX;
      cardsRef.current.scrollLeft = dragScrollStart + dx;
    };
    const onTouchEnd = () => setDragging(false);

    useEffect(() => {
      const handleMouseMove = (e) => {
        const dx = dragStartX - e.clientX;
        cardsRef.current.scrollLeft = dragScrollStart + dx;
      };
      const handleMouseUp = () => setDragging(false);
      const handleMouseLeave = () => setDragging(false);

      if (dragging) {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("mouseleave", handleMouseLeave);
      } else {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("mouseleave", handleMouseLeave);
      }
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [dragging, dragStartX, dragScrollStart]);

    // 判斷是否超出範圍
    useEffect(() => {
      const checkArrows = () => {
        if (cardsRef.current) {
          setShowArrows(
            cardsRef.current.scrollWidth > cardsRef.current.clientWidth,
          );
        }
        setIsMobile(window.innerWidth <= 600);
      };
      checkArrows();
      window.addEventListener("resize", checkArrows);
      return () => window.removeEventListener("resize", checkArrows);
    }, [items]);

    return (
      <div className="portal-row">
        <div className="cards-row-wrapper d-flex align-items-center">
          {!isMobile && showArrows && (
            <button
              className="arrow-btn left"
              onClick={handleLeft}
              aria-label="左移"
            >
              <svg
                width="18"
                height="40"
                viewBox="0 0 18 40"
                style={{ display: "block", margin: "auto" }}
              >
                <polygon points="13,8 5,20 13,32" fill="#888" />
              </svg>
            </button>
          )}
          <div
            className="cards d-flex"
            ref={cardsRef}
            style={{
              cursor: dragging ? "grabbing" : "grab",
              gap: 12,
              paddingBottom: 8,
              overflowX: "auto",
            }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {items.length === 0 ? (
              <div className="card">暫無資料</div>
            ) : (
              items.map((item) =>
                title === "Badge" ? (
                  <BadgeCard key={item.id} item={item} />
                ) : (
                  <ItemCard key={item.id} item={item} hideMapButton={hideMapButton} />
                ),
              )
            )}
          </div>
          {!isMobile && showArrows && (
            <button
              className="arrow-btn right"
              onClick={handleRight}
              aria-label="右移"
            >
              <svg
                width="18"
                height="40"
                viewBox="0 0 18 40"
                style={{ display: "block", margin: "auto" }}
              >
                <polygon points="5,8 13,20 5,32" fill="#888" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Container className="portal-container">
      <Row>
        <Col>
          <div className="portal-banner">
            <div className="banner-overlay">
              <div
                className="cross-buttons"
                role="navigation"
                aria-label="portal actions"
              >
                <button
                  className="cross-btn top"
                  onClick={() => navigate("/map")}
                  aria-label="查找地點"
                >
                  查找地點
                </button>
                <button
                  className="cross-btn left"
                  onClick={() => navigate("/photos")}
                  aria-label="上傳照片"
                >
                  上傳照片
                </button>
                <button
                  className="cross-btn right"
                  onClick={() => navigate("/events")}
                  aria-label="加入活動"
                >
                  加入活動
                </button>
                <button
                  className="cross-btn bottom"
                  onClick={() => navigate("/contact")}
                  aria-label="聯絡我們"
                >
                  聯絡我們
                </button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="portal-tabs"
            className="mb-3"
          >
            {tabs.map((t) => (
              <Tab eventKey={t.key} title={t.label} key={t.key}>
                <PortalRow title={t.label} items={t.items} />
              </Tab>
            ))}
          </Tabs>
        </Col>
      </Row>

      <Row>
        <Col
          md={6}
          className="d-flex flex-column justify-content-center align-items-start"
        >
          <h3>Share your plan to club members</h3>
          <p style={{ marginBottom: 0 }}>
            There are many variations of passages of Lorem Ipsum available, but
            the majority have suffered alteration in some form, by injected
            humour, or randomised words which don't look even slightly
            believable. If you are going to use a passage of Lorem Ipsum, you
            need to be sure there isn't anything embarrassing hidden in the
            middle of text.{" "}
          </p>
        </Col>
        <Col md={6}>
          <img src="default-cover.svg" alt="" className="img-fluid" />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <img src="default-cover.svg" alt="" className="img-fluid" />
        </Col>
        <Col
          md={6}
          className="d-flex flex-column justify-content-center align-items-start"
        >
          <h3>Share your plan to club members</h3>
          <p style={{ marginBottom: 0 }}>
            There are many variations of passages of Lorem Ipsum available, but
            the majority have suffered alteration in some form, by injected
            humour, or randomised words which don't look even slightly
            believable. If you are going to use a passage of Lorem Ipsum, you
            need to be sure there isn't anything embarrassing hidden in the
            middle of text.{" "}
          </p>
        </Col>
      </Row>

      <Row className="portal-badge mb-3">
        <Col>
          <h5 style={{ marginBottom: 8 }}>Badge</h5>
          <PortalRow title="Badge" items={badgeItems} />
        </Col>
      </Row>

      <Row className="portal-clinics-section mb-3">
        <Col>
          <h5 style={{ marginBottom: 8 }}>Discover</h5>
          <PortalRow title="附近診所" items={nearbyClinics} />
        </Col>
      </Row>

      <Row className="portal-pdc-section mb-3">
        <Col>
          <h5 style={{ marginBottom: 8 }}>Explore PDC</h5>
          <PortalRow title="ExplorePDC" items={recentPdcs} hideMapButton={true} />
        </Col>
      </Row>

      <Row className="portal-articles-section mb-3">
        <Col>
          <h5 style={{ marginBottom: 8 }}>Discover New</h5>
          <PortalRow title="Discover New" items={recentArticles} hideMapButton={true} />
        </Col>
      </Row>

      <Row className="portal-footer mt-3">
        <Col className="d-flex justify-content-between align-items-center">
          <div className="social-links d-flex gap-2">
            <a
              className="social-icon facebook"
              href="https://www.facebook.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 5 3.66 9.12 8.44 9.88v-6.99H7.9v-2.89h2.54V9.41c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.23.2 2.23.2v2.45h-1.25c-1.23 0-1.61.77-1.61 1.56v1.87h2.74l-.44 2.89h-2.3V22C18.34 21.12 22 17 22 12z" />
              </svg>
            </a>
            <a
              className="social-icon instagram"
              href="https://www.instagram.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm5 5.9A4.1 4.1 0 1 1 7.9 12 4.1 4.1 0 0 1 12 7.9zm4.5-.4a1 1 0 1 1 1-1 1 1 0 0 1-1 1zM12 9.5A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5z" />
              </svg>
            </a>
            <a
              className="social-icon twitter"
              href="https://twitter.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Twitter"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.46 6c-.77.35-1.6.59-2.46.7a4.3 4.3 0 0 0 1.88-2.38 8.59 8.59 0 0 1-2.72 1.04 4.28 4.28 0 0 0-7.29 3.9A12.14 12.14 0 0 1 3.15 4.6a4.28 4.28 0 0 0 1.33 5.71 4.24 4.24 0 0 1-1.94-.54v.05a4.28 4.28 0 0 0 3.43 4.19 4.3 4.3 0 0 1-1.93.07 4.29 4.29 0 0 0 4 2.97A8.6 8.6 0 0 1 2 19.54 12.13 12.13 0 0 0 8.29 21c7.55 0 11.68-6.26 11.68-11.69 0-.18-.01-.35-.02-.53A8.36 8.36 0 0 0 22.46 6z" />
              </svg>
            </a>
          </div>
          <div className="contact">
            <a
              className="contact-icon"
              href="mailto:info@petbuddy.example"
              aria-label="Email"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </a>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PortalHome;
